import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaTrash, FaEdit, FaSave, FaRegEdit } from "react-icons/fa"; // Import FaTrash and FaEdit icons
import { MdCancel } from "react-icons/md";
import ReactQuill from "react-quill";
import Loader from "../../Components/Services/Loader";
import axios from "axios";
import { useSelector } from "react-redux";
import "./IngredientItem.css";
import { toast } from "react-toastify";
import { TiTick } from "react-icons/ti";
import { useModal } from "../../Hooks/modalContext";
import SymbolForm from "../../Components/Database/SymbolForm";

const IngredientItem = () => {
  const { ing_item_code } = useParams();
  const [isInsertingSymbol, setIsInsertingSymbol] = useState(false);
  const [ingredients, setIngredients] = useState([]);
  const [sourceInfo, setSourceInfo] = useState([]);
  const [sourceType, setSourceType] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false); // Track edit mode
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [originalIngredient, setOriginalIngredient] = useState(null);

  const [sourceTypeOther, setSourceTypeOther] = useState(false);
  const [sourceTypeSelectedValue, setSourceTypeSelectedValue] = useState("");
  const [sourceTypeCustomInput, setSourceTypeCustomInput] = useState("");

  const [countryOriginData, setCountryOriginData] = useState([]);
  const [countrySelectedValue, setCountrySelectedValue] = useState("");
  const [countryCustomInput, setCountryCustomInput] = useState("");
  const [countryOther, setCountryOther] = useState(false);
  const [countryOrigin, setCountryOrigin] = useState("");
  const [countryIndex, setCountryIndex] = useState(null);
  const [countryInputs, setCountryInputs] = useState({});

  const [selectedSymbols, setSelectedSymbols] = useState([
    { symbol: "0", symbol_code: "0000", symbol_id: 0 },
  ]); // Track the selected symbol
  const [symbolData, setSymbolData] = useState([]); // Array of available symbols

  const quillRefs = useRef([]);

  const { role } = useSelector((states) => states.auth);

  const fetchIngredientDetails = useCallback(async () => {
    if (!ing_item_code) {
      setError("Ingredient code is missing.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URI}/elixir/fetch_ingredient/${ing_item_code}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const { data } = await response.json();

      setSourceInfo(data[0].source);
      setCountryOrigin(data[0].country_origin);

      if (data && data.length > 0) {
        setIngredients(data);
        setError(null);
      } else {
        setError("No data found for this ingredient.");
      }
    } catch (error) {
      setError("Error fetching ingredient details: " + error.message);
    } finally {
      setLoading(false);
    }
  }, [ing_item_code]);

  const getAllInfo = async () => {
    const response = await axios.get(
      `${process.env.REACT_APP_BASE_URI}/elixir/all-info`,
      {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (response.status !== 200) {
      console.log("Network Error");
    }

    setSourceType(response.data.source_type);
    setCountryOriginData(response.data.country_origin);
    setSymbolData(response.data.symbols);
  };

  useEffect(() => {
    getAllInfo();
  }, []);

  const placeholderSymbol = { symbol: "0", symbol_code: "0000", symbol_id: 0 };

  useEffect(() => {
    if (symbolData && ingredients.length) {
      const ingredient = ingredients[0]; // Only care about first ingredient
      const matchedSymbol = symbolData.find(
        (symbol) => symbol.symbol_id === ingredient.symbol_id
      );
      setSelectedSymbols([matchedSymbol || placeholderSymbol]);
    }
  }, [symbolData, ingredients]);

  const deleteIngredient = async (rmCode) => {
    if (!ing_item_code || !rmCode) return;

    const confirmDelete = window.confirm(
      "Are you sure you want to delete this ingredient?"
    );
    if (!confirmDelete) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URI}/elixir/ingredient/${ing_item_code}?rm_code=${rmCode}`,
        {
          method: "DELETE",
          headers: {
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete ingredient.");
      }

      setIngredients((prevIngredients) =>
        prevIngredients.filter((ingredient) => ingredient.rm_code !== rmCode)
      );

      alert("Ingredient deleted successfully.");

      // Redirect only if all rows are deleted
      setIngredients((prev) => {
        if (prev.length === 0) {
          navigate("/database/ingredient");
        }
        return prev;
      });
    } catch (error) {
      setError("Error deleting ingredient: " + error.message);
    }
  };

  const handleEdit = () => {
    setOriginalIngredient(ingredients);
    setEditMode(true); // Enable edit mode
  };

  const handleCancel = () => {
    setIngredients(originalIngredient);
    setSourceTypeSelectedValue("");
    setCountrySelectedValue("");
    setCountryInputs({});
    setEditMode(!editMode);
    setSourceInfo(originalIngredient[0].source);
  };

  const handleSave = async () => {
    try {
      // const rm_codes = ingredients.map((ing) => ing.rm_code)
      // const vendors = ingredients.map((ing) => ing.vendor)

      const isValid = ingredients.some(
        (ing) =>
          !ing.rm_code.trim() ||
          !ing.vendor.trim() ||
          !ing.country_origin.trim()
      );

      if (isValid) {
        toast.error("RM Code, Vendor, and Country cannot be empty!");
        return; // Stop API call
      }

      const rowUpdates = ingredients.map((ing) => ({
        rm_code: ing.rm_code.trim(),
        vendor: ing.vendor.trim(),
        country_origin: ing.country_origin.trim(),
      }));

      // Prepare the data to be sent in the PATCH request
      const payload = {
        ing_item_code: ingredients[0].ing_item_code.trim(), // Assuming you have this in the first ingredient
        global_updates: {
          ing_item_code: ingredients[0].ing_item_code,
          ing_name: ingredients[0].ing_name.trim(),
          symbol_id: selectedSymbols[0].symbol_id,
          cas_num: ingredients[0].cas_num.trim(),
          ec_num: ingredients[0].ec_num.trim(),
          ing_type: "string",
          source_type: ingredients[0].source_type.trim(),
          source: ingredients[0].source.trim(),
          calories: Number(ingredients[0].calories),
          fat: Number(ingredients[0].fat),
          carbohydrates: Number(ingredients[0].carbohydrates),
          protein: Number(ingredients[0].protein),
          moisture: Number(ingredients[0].moisture),
          ash: Number(ingredients[0].ash),
          updated_at: new Date().toISOString(),
        },
        row_updates: rowUpdates,
      };

      // //Convert this payload into SearchParams
      // const queryParams = new URLSearchParams();

      // Object.entries(payload).forEach(([key, value]) => {
      //   if (Array.isArray(value)) {
      //     value.forEach(v => queryParams.append(key, v)); // Append each value separately
      //   } else {
      //     queryParams.append(key, value);
      //   }
      // });

      // payload.rm_code = rm_codes;

      // // Sending PATCH request to update the ingredient
      // const response = await fetch(
      //   `${process.env.REACT_APP_BASE_URI}/elixir/ingredient/${ing_item_code}?rm_code=${ingredients[0].rm_code}`,
      //   {
      //     method: "PATCH",
      //     headers: {
      //       "Content-Type": "application/json",
      //       accept: "application/json",
      //     },
      //     credentials: 'include',
      //     body: JSON.stringify(payload),
      //   }
      // );

      const response = await axios.patch(
        `${process.env.REACT_APP_BASE_URI}/elixir/ingredient/${ing_item_code}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          withCredentials: true,
        }
      );

      if (!response.status === 200) {
        throw new Error("Failed to update ingredient details.");
      }

      alert("Ingredient details updated successfully.");
      toast.success("Ingredients Updated Successfully");

      const updatedIngredients = {
        ...ingredients[0],
        updated_at: new Date().toISOString(),
      };

      setIngredients((prev) =>
        prev.map((item) =>
          rowUpdates.some((updated) => updated.rm_code === item.rm_code)
            ? {
                ...item,
                ...rowUpdates.find(
                  (updated) => updated.rm_code === item.rm_code
                ),
                updated_at: new Date().toISOString(),
              }
            : item
        )
      );

      setEditMode(false); // Disable edit mode after successful save
      setTimeout(() => {
        navigate(`/ingredient/${ingredients[0].ing_item_code}`);
      }, 500);
      setCountryInputs({});
    } catch (error) {
      // setError("Error saving ingredient details: " + error.message);
      if (error.response) {
        console.error("Server responded with error:", error.response.data);
        if (error.response.data.detail[0].msg.includes("Field required")) {
          alert("Please check input fields and try again");
        }
        // alert(`Error: ${error.response.data.detail[0].msg || "Failed to update ingredient"}`);
      } else if (error.request) {
        console.error("No response received from server:", error.request);
        alert("Error: No response from server. Please check your connection.");
      } else {
        console.error("Unexpected error:", error.message);
        alert(`Unexpected error: ${error.message}`);
      }
    }
  };

  const createSymbolRegex = (symbols) => {
    const escapedSymbols = symbols
      .filter((s) => s.symbol.length > 1 || /[^\d]/.test(s.symbol)) // Exclude pure digits like "0"
      .map((s) => s.symbol.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    // .map((s) =>
    //   s.symbol.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    // );
    return new RegExp(escapedSymbols.join("|"), "g"); //TM|C|0
  };

  // const [isOpen, setIsOpen ] = useModal();
  const { openModal } = useModal();
  const handleClick = (e) => {
    openModal(<SymbolForm />);
  };

  const sanitizeHtmlContent = (html, symbolRegex) => {
    return html
      .replace(/<span\s+class=["']ql_cursor["']\s*><\/span>/g, "") // Remove cursor spans
      .replace(/[\u00A0\u200B\uFEFF]/g, "") // Remove invisible spaces
      .replace(new RegExp(`\\s+(?=${symbolRegex.source})`, "g"), ""); // Remove space before symbol
  };

  const handleSymbolChange = (symbolObj, index) => {
    const quillInstance = quillRefs.current[index]?.getEditor();
    if (!quillInstance) return;
  
    quillInstance.focus();
    const selection = quillInstance.getSelection(true); // Force selection update
    let cursorIndex = selection?.index ?? 0; // Default to start if no selection
  
    const isSelected = selectedSymbols.some(
      (s) => s.symbol_id === symbolObj.symbol_id
    );
  
    const updatedIngredients = [...ingredients];
  
    setIsInsertingSymbol(true);
  
    if (!isSelected) {
      // Select new symbol
      setSelectedSymbols([symbolObj]);
      updatedIngredients[index].symbol_id = symbolObj.symbol_id;
  
      // Get current text and find existing symbols
      let fullText = quillInstance.getText();
      if (fullText.endsWith("\n")) fullText = fullText.slice(0, -1);
      const symbolRegex = createSymbolRegex(symbolData);
      const match = fullText.match(symbolRegex);
  
      if (match) {
        // Delete existing symbol
        const matchIndex = fullText.indexOf(match[0]);
        quillInstance.deleteText(matchIndex, match[0].length);
        // Adjust cursor index if it was after the deleted symbol
        if (cursorIndex > matchIndex) {
          cursorIndex = Math.max(cursorIndex - match[0].length, matchIndex);
        }
      }
  
      // Insert new symbol at cursor
      const rawCode = symbolObj?.symbol_code;
      const codePoint = parseInt(rawCode, 16);
      if (!Number.isInteger(codePoint) || codePoint <= 0) {
        console.warn("Invalid hex symbol_code:", rawCode);
        setIsInsertingSymbol(false);
        return;
      }
      const char = String.fromCodePoint(codePoint);
      quillInstance.insertText(cursorIndex, char, "user");
      quillInstance.setSelection(cursorIndex); // Cursor after symbol
  
      // Update ingredient name
      const rawHTML = quillInstance.root.innerHTML;
      const cleanHTML = sanitizeHtmlContent(rawHTML, symbolRegex);
      updatedIngredients[index].ing_name = cleanHTML;
      setIngredients(updatedIngredients);
    } else {
      // Deselect symbol
      setSelectedSymbols([placeholderSymbol]);
      updatedIngredients[index].symbol_id = 0;
  
      // Remove existing symbol
      let fullText = quillInstance.getText();
      if (fullText.endsWith("\n")) fullText = fullText.slice(0, -1);
      const symbolRegex = createSymbolRegex(symbolData);
      const match = fullText.match(symbolRegex);
  
      if (match) {
        const matchIndex = fullText.indexOf(match[0]);
        quillInstance.deleteText(matchIndex, match[0].length);
      }
  
      // Update ingredient name
      const rawHTML = quillInstance.root.innerHTML;
      const cleanHTML = sanitizeHtmlContent(rawHTML, symbolRegex);
      updatedIngredients[index].ing_name = cleanHTML;
      setIngredients(updatedIngredients);
    }
  
    setIsInsertingSymbol(false);
  };


  const getPlainTextFromQuill = (htmlContent) => {
    const tempElement = document.createElement("div");
    tempElement.innerHTML = htmlContent;
    return tempElement.textContent || tempElement.innerText || "";
  };

   const handleIngredientNameChange = (htmlContent, index) => {
    if (isInsertingSymbol) {
      console.log("Currently inserting symbol, skipping...");
      return;
    }

    const placeholderSymbol = {
      symbol: "0",
      symbol_code: "0000",
      symbol_id: 0,
    };

    // Step 1: Remove invisible characters and ql_cursor spans
    let cleanedHtml = htmlContent
      .replace(/<span\s+class=["']ql_cursor["']\s*><\/span>/g, "")
      .replace(/[\u00A0\u200B\uFEFF]/g, "")
      .replace(/\s+(?=(${createSymbolRegex(symbolData).source}))/g, ""); // Remove space before symbols

    const plainText = getPlainTextFromQuill(cleanedHtml).trim();

    const symbolMatch = plainText.match(createSymbolRegex(symbolData));

    
    let updatedNames = [...ingredients];
    let updatedIngredients = [...ingredients];

    // Update ingredient name
    updatedIngredients[index].ing_name = cleanedHtml;
    setIngredients(updatedIngredients);

   if (symbolMatch) {
    const foundSymbol = symbolData.find(
      (symbolObj) => symbolMatch[0] === symbolObj.symbol
    );
    if (
      foundSymbol &&
      foundSymbol.symbol_id !== selectedSymbols[0]?.symbol_id
    ) {
      setSelectedSymbols([foundSymbol]);
      updatedIngredients[index].symbol_id = foundSymbol.symbol_id;
    }
  } else if (selectedSymbols[0]?.symbol_id !== placeholderSymbol.symbol_id) {
    setSelectedSymbols([placeholderSymbol]);
    updatedIngredients[index].symbol_id = 0;
  }

  setIngredients(updatedIngredients);
      
  };
;

 

  const handleInputChange = (key, value) => {
    setIngredients((prev) =>
      prev.map((ingredient) => ({
        ...ingredient, // Ensure we maintain object structure
        [key]: value, // Update the specific key with the new value
      }))
    );
    // const updatedIngredients = [...prev];
    // updatedIngredients[0] = {
    //   ...updatedIngredients[0],
    //   [key]: value,  // Store as string
    // }; // Update only the first ingredient (or adjust as needed)

    // if (updatedIngredients.length > 0) {
    //   setSourceInfo(updatedIngredients[0]['source']);
    //   setCountryOrigin(updatedIngredients[0]['country_origin']);
    // }

    // // setSourceInfo(updatedIngredients[0]['source']);
    // // setCountryOrigin(updatedIngredients[0]['country_origin']);
    // return updatedIngredients;

    // Assuming all ingredients should have the same `source` and `country_origin`, use the first one
    // or setCountryOrigin(prev[0]?.country_origin || "");

    if (key === "source") {
      setSourceInfo(value);
    }
    if (key === "country_origin") {
      setCountryOrigin(value);
    }
  };

  const handleRmCodeAndVendorChange = (key, value, index) => {
    setIngredients((prev) => {
      return prev.map((ingredient, i) => {
        // If updating `rm_code`, update only the ingredient at `index`
        if (key === "rm_code" && i === index) {
          return { ...ingredient, rm_code: value };
        }

        // If updating `vendor`, update only if the ingredient's `rm_code` matches the one at `index`
        if (key === "vendor" && ingredient.rm_code === prev[index]?.rm_code) {
          return { ...ingredient, vendor: value };
        }

        return ingredient;
      });
    });
  };

  // const handleVendorInputChange = (index, value) => {
  //   setIngredients((prev) => {
  //     const updatedIngredients = [...prev];
  //     updatedIngredients[index].country_origin = value; // Update the country_origin of the vendor
  //     return updatedIngredients;
  //   });
  // };

  // const formatDate = (dateString) => {
  //   if (!dateString) return "N/A";
  //   const date = new Date(dateString);
  //   if (isNaN(date.getTime())) return "N/A";
  //   return date.toLocaleDateString("en-IN");
  // };

  const formatToIST = (dateString) => {
    if (!dateString) return "N/A";

    const date = new Date(dateString);

    // Helper to get ordinal suffix
    const getOrdinalSuffix = (day) => {
      if (day > 3 && day < 21) return "th"; // Covers 11th to 19th
      switch (day % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    };

    // Convert date to IST correctly
    const options = {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };

    const formatter = new Intl.DateTimeFormat("en-IN", options);
    const parts = formatter.formatToParts(date);

    const day = parts.find((p) => p.type === "day").value;
    const month = parts.find((p) => p.type === "month").value;
    const year = parts.find((p) => p.type === "year").value;
    const time = `${parts.find((p) => p.type === "hour").value}:${
      parts.find((p) => p.type === "minute").value
    } ${parts.find((p) => p.type === "dayPeriod").value}`;

    return `${parseInt(day)}${getOrdinalSuffix(
      parseInt(day)
    )} ${month} ${year}, ${time}`;
  };

  const handleSourceTypeChange = (e) => {
    const { value } = e.target;

    if (value === "Others (Please Specify)") {
      setSourceTypeOther(true);
      setSourceTypeSelectedValue("");
    } else {
      setSourceTypeOther(false);
      setSourceTypeCustomInput("");
      setSourceTypeSelectedValue(value);
      handleInputChange("source_type", value);
    }
  };

  const handleSourceTypeEnterPress = (e) => {
    if (e.key === "Enter" && sourceTypeCustomInput.trim()) {
      const customValue = sourceTypeCustomInput.trim();
      setSourceTypeSelectedValue(customValue);

      handleInputChange("source_type", customValue);

      setSourceTypeCustomInput("");
      setSourceTypeOther(false);
    }
  };

  const handleCountryChange = (e, index) => {
    const { value } = e.target;

    setIngredients((prev) =>
      prev.map((ingredient, i) => {
        if (i === index) {
          return {
            ...ingredient,
            country_origin: value === "Others (Please Specify)" ? "" : value,
          };
        } else {
          return ingredient;
        }
      })
    );

    setCountryInputs((prev) => ({
      ...prev,
      [index]: {
        countryOther: value === "Others (Please Specify)",
        countryCustomInput: "",
        countrySelectedValue: "",
      },
    }));

    // if (value === "Others (Please Specify)") {
    //   setCountryIndex(index);
    //   setCountryOther(true);
    //   setCountrySelectedValue("");
    // } else {
    //   setCountryIndex(null);
    //   setCountryOther(false);
    //   setCountryCustomInput("");
    //   setCountrySelectedValue(value);
    // }
  };

  const handleCountryEnterPress = (e, index) => {
    if (e.key === "Enter" && countryInputs[index]?.countryCustomInput.trim()) {
      const customValue = countryInputs[index]?.countryCustomInput.trim();
      // setCountrySelectedValue(customValue);

      setIngredients((prev) =>
        prev.map((ingredient, i) =>
          i === index
            ? { ...ingredient, country_origin: customValue }
            : ingredient
        )
      );

      // // handleInputChange("country_origin", customValue);
      // setCountryIndex(index);
      // setCountryCustomInput("");
      // setCountryOther(false);

      setCountryInputs((prev) => ({
        ...prev,
        [index]: {
          countryOther: false, // Close the input field
          countryCustomInput: "", // Clear input field
          countrySelectedValue: customValue, // Store the custom value
        },
      }));
    }
  };

  useEffect(() => {
    fetchIngredientDetails();
  }, [fetchIngredientDetails]);

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="min-h-screen bg-slate-700 p-4 text-white flex items-center justify-center relative">
      <button
        onClick={() => navigate("/database/ingredient")}
        className="absolute top-4 left-4 bg-gray-800 text-white py-2 px-4 rounded shadow hover:bg-gray-600 z-10 max-md:top-2 max-md:left-2 max-md:px-3 max-md:py-1 max-md:text-sm"
      >
        Back
      </button>

      {error ? (
        <div className="text-red-500">{error}</div>
      ) : (
        <div className="bg-gray-800 rounded-lg shadow-lg p-6 w-full max-w-4xl overflow-x-auto max-sm:mt-14 mt-16">
          {ingredients.length > 0 && (
            <>
              <div className="bg-gray-700 rounded-lg shadow-md p-4 mb-4">
                <div className="grid grid-cols-[1fr_2fr] max-sm:grid-cols-1 gap-4 items-center mt-3">
                  {editMode ? (
                    <>
                      <div>
                        <strong>Ingredient Item Code : </strong>
                      </div>
                      <div>
                        <span className="text-gray-300">
                          {ingredients[0].ing_item_code || "N/A"}
                        </span>
                      </div>
                      <div>
                        <strong>Ingredient Name : </strong>
                      </div>
                      <div>
                        {role === "admin" && editMode ? (
                          // <input
                          //   type="text"
                          //   name="ing_name"
                          //   value={ingredients[0].ing_name}
                          //   onChange={(event) => handleInputChange("ing_name", event.target.value)}
                          //   className="border border-gray-400 rounded px-4 py-2 bg-gray-700 text-gray-200"
                          //   placeholder="Enter ingredient name"
                          //   required
                          // />

                          <div className="flex flex-col  items-center w-full sm:w-full">
                            <ReactQuill
                              ref={(el) => (quillRefs.current[0] = el)}
                              name="ing_name"
                              value={ingredients[0]?.ing_name || ""}
                              onChange={(content) =>
                                handleIngredientNameChange(content.trim(), 0)
                              }
                              theme="snow"
                              // className="bg-gray-500 text-white  w-full "
                              className=" text-gray-200 w-full"
                              required
                            />

                            <div className="flex flex-wrap gap-4 my-2">
                              {symbolData.length > 0 ? (
                                symbolData
                                  .filter((item) => item.symbol_id !== 0)
                                  .map((symbolObj) => (
                                    <label
                                      key={symbolObj.symbol_id}
                                      className="flex items-center space-x-2"
                                    >
                                      <input
                                        type="checkbox"
                                        name="symbol"
                                        checked={selectedSymbols.some(
                                          (selected) =>
                                            selected.symbol_id ===
                                            symbolObj.symbol_id
                                        )}
                                        placeholder={<TiTick />}
                                        onChange={() =>
                                          handleSymbolChange(symbolObj, 0)
                                        }
                                        className={`rounded-[100%] appearance-none color-white w-3 h-3 bg-white checked:bg-blue-500`}
                                      />
                                      <span className="text-xl">
                                        {symbolObj.symbol}
                                      </span>
                                    </label>
                                  ))
                              ) : (
                                <p className="text-gray-400">
                                  No symbols available
                                </p>
                              )}
                              <button
                                type="button"
                                onClick={() => handleClick()}
                                className="bg-blue-500 w-10 rounded-full mt-1"
                              >
                                +
                              </button>
                            </div>
                          </div>
                        ) : (
                          <span
                            dangerouslySetInnerHTML={{
                              __html: ingredients[0]?.ing_name || "N/A",
                            }}
                          />
                        )}
                      </div>
                    </>
                  ) : (
                    <h2 className="text-xl font-semibold">
                      <span
                        dangerouslySetInnerHTML={{
                          __html: ingredients[0]?.ing_name || "N/A",
                        }}
                      />
                    </h2>
                  )}
                </div>

                <div className="grid grid-cols-[1fr_2fr] max-sm:grid-cols-1 gap-4 items-center mt-3">
                  {!editMode && (
                    <>
                      <div>
                        <strong>Ingredient Item Code : </strong>
                      </div>
                      <div>
                        <span>{ingredients[0].ing_item_code || "N/A"}</span>
                      </div>
                    </>
                  )}

                  <div>
                    <strong>CAS Number : </strong>
                  </div>
                  <div>
                    {(role === "admin" || role === "user") && editMode ? (
                      <textarea
                        value={ingredients[0].cas_num} // Correctly referencing the first ingredient
                        onChange={(e) =>
                          handleInputChange("cas_num", e.target.value)
                        }
                        className="border border-gray-400 bg-gray-600 text-white p-2 rounded w-full h-auto resize-none"
                      />
                    ) : (
                      <span style={{ whiteSpace: "pre-line" }}>
                        {ingredients[0].cas_num}
                      </span>
                    )}
                  </div>

                  <div>
                    {" "}
                    <strong>EC Number : </strong>{" "}
                  </div>
                  <div>
                    {(role === "admin" || role === "user") && editMode ? (
                      <textarea
                        value={ingredients[0].ec_num}
                        onChange={(e) =>
                          handleInputChange("ec_num", e.target.value)
                        }
                        className="border border-gray-400 bg-gray-600 text-white p-2 rounded w-full h-auto resize-none"
                      />
                    ) : (
                      <span style={{ whiteSpace: "pre-line" }}>
                        {ingredients[0].ec_num}
                      </span>
                    )}
                  </div>

                  {/* <p>
                  <strong>Ingredient Type:</strong>
                  {editMode ? (
                    <input
                      type="text"
                      value={ingredients[0].ing_type}
                      onChange={(e) =>
                        handleInputChange("ing_type", e.target.value)
                      }
                      className="bg-gray-600 text-white p-2 rounded w-full"
                    />
                  ) : (
                    ingredients[0].ing_type || "N/A"
                  )}
                </p> */}
                  <div>
                    <strong>Source Type: </strong>
                  </div>
                  <div>
                    {(role === "admin" || role === "user") && editMode ? (
                      <div className="flex flex-col justify-center">
                        {/* Dropdown for predefined values */}
                        <select
                          value={
                            sourceTypeSelectedValue ||
                            ingredients[0].source_type ||
                            ""
                          }
                          onChange={handleSourceTypeChange}
                          className="border border-gray-400 rounded px-4 py-2 bg-gray-700 text-gray-200 w-full"
                        >
                          <option value="">Select Source Type</option>
                          {sourceType.map((type, index) => (
                            <option key={index} value={type}>
                              {type}
                            </option>
                          ))}
                          <option value="Others (Please Specify)">
                            Others (Please Specify)
                          </option>
                        </select>

                        {/* Custom Input Field for "Others" Option */}
                        {sourceTypeOther && (
                          <input
                            type="text"
                            value={sourceTypeCustomInput}
                            onChange={(e) =>
                              setSourceTypeCustomInput(e.target.value)
                            }
                            onKeyDown={handleSourceTypeEnterPress}
                            placeholder="Enter custom source type and press Enter"
                            className="border border-gray-400 rounded px-4 py-2 bg-gray-700 text-gray-200 mt-2"
                            autoFocus
                          />
                        )}

                        {/* Display Selected Custom Value Below Select */}
                        {sourceTypeSelectedValue &&
                          !sourceType.includes(sourceTypeSelectedValue) &&
                          !sourceTypeOther && (
                            <span className="mt-2 text-gray-300">
                              Selected Source Type:{" "}
                              <span className="text-md font-bold">
                                {sourceTypeSelectedValue}
                              </span>
                            </span>
                          )}
                      </div>
                    ) : (
                      // Static text when not in edit mode
                      <span>{ingredients[0].source_type || "N/A"}</span>
                    )}
                  </div>

                  <div>
                    {" "}
                    <strong>Source : </strong>{" "}
                  </div>
                  <div>
                    {(role === "admin" || role === "user") && editMode ? (
                      <ReactQuill
                        value={ingredients[0].source || ""}
                        onChange={(value) => {
                          handleInputChange("source", value);
                        }}
                        theme="snow"
                        className=" text-gray-200 w-full"
                      />
                    ) : (
                      (
                        <span
                          style={{ display: "inline-block" }}
                          dangerouslySetInnerHTML={{ __html: sourceInfo }}
                        />
                      ) || "N/A"
                    )}
                  </div>

                  <div>
                    {" "}
                    <strong>Calories : </strong>{" "}
                  </div>
                  <div>
                    {(role === "admin" || role === "user") && editMode ? (
                      <input
                        name="calories"
                        type="number"
                        step="any" // Allows decimals
                        min="0"
                        value={ingredients[0].calories || ""}
                        onChange={(e) =>
                          handleInputChange("calories", e.target.value)
                        }
                        className="border border-gray-400 rounded px-4 py-2 bg-gray-600 text-gray-200 mt-2 w-full"
                        autoFocus
                      />
                    ) : (
                      <span style={{ whiteSpace: "pre-line" }}>
                        {String(ingredients[0].calories) || "N/A"}
                      </span>
                    )}
                  </div>

                  <div>
                    {" "}
                    <strong>Fats : </strong>{" "}
                  </div>
                  <div>
                    {(role === "admin" || role === "user") && editMode ? (
                      <input
                        name="fat"
                        type="number"
                        step="any"
                        min="0"
                        value={String(ingredients[0].fat) || ""}
                        onChange={(e) =>
                          handleInputChange("fat", e.target.value)
                        }
                        className="border border-gray-400 rounded px-4 py-2 bg-gray-600 text-gray-200 mt-2 w-full"
                        autoFocus
                      />
                    ) : (
                      <span style={{ whiteSpace: "pre-line" }}>
                        {String(ingredients[0].fat) || "N/A"}
                      </span>
                    )}
                  </div>
                  <div>
                    {" "}
                    <strong>Carbohydrates : </strong>{" "}
                  </div>
                  <div>
                    {(role === "admin" || role === "user") && editMode ? (
                      <input
                        name="carbohydrates"
                        type="number"
                        step="any"
                        min="0"
                        value={String(ingredients[0].carbohydrates) || ""}
                        onChange={(e) =>
                          handleInputChange("carbohydrates", e.target.value)
                        }
                        className="border border-gray-400 rounded px-4 py-2 bg-gray-600 text-gray-200 mt-2 w-full"
                        autoFocus
                      />
                    ) : (
                      <span style={{ whiteSpace: "pre-line" }}>
                        {String(ingredients[0].carbohydrates) || "N/A"}
                      </span>
                    )}
                  </div>

                  <div>
                    {" "}
                    <strong>Protein : </strong>{" "}
                  </div>
                  <div>
                    {(role === "admin" || role === "user") && editMode ? (
                      <input
                        name="protein"
                        type="number"
                        step="any"
                        min="0"
                        value={String(ingredients[0].protein) || ""}
                        onChange={(e) =>
                          handleInputChange("protein", e.target.value)
                        }
                        className="border border-gray-400 rounded px-4 py-2 bg-gray-600 text-gray-200 mt-2 w-full"
                        autoFocus
                      />
                    ) : (
                      <span style={{ whiteSpace: "pre-line" }}>
                        {String(ingredients[0].protein) || "N/A"}
                      </span>
                    )}
                  </div>
                  <div>
                    {" "}
                    <strong>Moisture : </strong>{" "}
                  </div>
                  <div>
                    {(role === "admin" || role === "user") && editMode ? (
                      <input
                        name="moisture"
                        type="number"
                        step="any"
                        min="0"
                        value={String(ingredients[0].moisture) || ""}
                        onChange={(e) =>
                          handleInputChange("moisture", e.target.value)
                        }
                        className="border border-gray-400 rounded px-4 py-2 bg-gray-600 text-gray-200 mt-2 w-full"
                        autoFocus
                      />
                    ) : (
                      <span style={{ whiteSpace: "pre-line" }}>
                        {String(ingredients[0].moisture) || "N/A"}
                      </span>
                    )}
                  </div>

                  <div>
                    {" "}
                    <strong>Ash : </strong>{" "}
                  </div>
                  <div>
                    {(role === "admin" || role === "user") && editMode ? (
                      <input
                        name="ash"
                        type="number"
                        step="any"
                        min="0"
                        value={String(ingredients[0].ash) || ""}
                        onChange={(e) =>
                          handleInputChange("ash", e.target.value)
                        }
                        className="border border-gray-400 rounded px-4 py-2 bg-gray-600 text-gray-200 mt-2 w-full"
                        autoFocus
                      />
                    ) : (
                      <span style={{ whiteSpace: "pre-line" }}>
                        {String(ingredients[0].ash) || "N/A"}
                      </span>
                    )}
                  </div>

                  <div>
                    <strong>Created at : </strong>{" "}
                  </div>
                  <div>{formatToIST(ingredients[0].created_at)}</div>

                  <div>
                    {" "}
                    <strong>Updated at : </strong>{" "}
                  </div>
                  <div>{formatToIST(ingredients[0].updated_at)}</div>
                </div>
              </div>

              <h1 className="text-2xl mb-4" style={{ whiteSpace: "nowrap" }}>
                Vendors for{" "}
                <span
                  dangerouslySetInnerHTML={{
                    __html:
                      ingredients[0]?.ing_name.replace(/<\/?p>/g, "") || "N/A",
                  }}
                />
              </h1>
              <div className="mb-6">
                <h2 className="text-xl mb-2">Vendors</h2>

                {/* Make table container scrollable on small screens */}
                <div className="overflow-x-auto">
                  <table className="w-full min-w-max border-separate border border-gray-600">
                    <thead>
                      <tr>
                        <th className="border border-gray-500 px-2 sm:px-4 py-2 text-sm sm:text-base">
                          RM Code
                        </th>
                        <th className="border border-gray-500 px-2 sm:px-4 py-2 text-sm sm:text-base">
                          Vendor
                        </th>
                        <th className="border border-gray-500 px-2 sm:px-4 py-2 text-sm sm:text-base">
                          Country Origin
                        </th>
                        {role === "admin" && (
                          <>
                            <th className="border border-gray-500 px-2 sm:px-4 py-2 text-sm sm:text-base">
                              Actions
                            </th>
                            <th className="border border-gray-500 px-2 sm:px-4 py-2 text-sm sm:text-base">
                              Delete
                            </th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {ingredients.map((ingredient, index) => (
                        <tr key={index} className=" text-center">
                          <td className="border border-gray-500 px-2 sm:px-4 py-2 text-sm sm:text-base">
                            {role === "admin" && editMode ? (
                              <input
                                type="text"
                                name="rm_code"
                                value={ingredient.rm_code}
                                onChange={(e) =>
                                  handleRmCodeAndVendorChange(
                                    "rm_code",
                                    e.target.value,
                                    index
                                  )
                                }
                                maxLength={15}
                                className="border border-gray-600 rounded px-4 py-2 bg-gray-700 text-gray-200"
                                placeholder="RM-XXX-XXX"
                                required
                              />
                            ) : (
                              <span className="block w-full text-center text-sm sm:text-base">
                                {ingredient.rm_code || "N/A"}
                              </span>
                            )}
                          </td>
                          <td className="border border-gray-500 px-2 sm:px-4 py-2 text-sm sm:text-base">
                            {role === "admin" && editMode ? (
                              <input
                                type="text"
                                name="vendor"
                                value={ingredient.vendor}
                                onChange={(e) =>
                                  handleRmCodeAndVendorChange(
                                    "vendor",
                                    e.target.value,
                                    index
                                  )
                                }
                                className="border border-gray-600 rounded px-4 py-2 bg-gray-700 text-gray-200"
                                placeholder="Enter Vendor Name"
                                required
                              />
                            ) : (
                              <span className="block w-full text-center text-sm sm:text-base">
                                {ingredient.vendor || "Vendor Unknown"}
                              </span>
                            )}
                          </td>
                          <td className="border border-gray-500 px-2 sm:px-4 py-2 text-sm sm:text-base h-10">
                            {(role === "admin" || role === "user") &&
                            editMode ? (
                              <div className="flex flex-col justify-center dropdown">
                                {/* Scrollable Dropdown */}
                                <select
                                  value={
                                    countryInputs[index]?.countryOther
                                      ? "Others (Please Specify)"
                                      : ingredient.country_origin
                                  }
                                  onChange={(e) =>
                                    handleCountryChange(e, index)
                                  }
                                  className="cursor-pointer rounded px-2 sm:px-4 py-2 bg-gray-700 text-gray-200 w-full overflow-y-auto selective"
                                  style={{
                                    maxHeight: "150px",
                                    overflowY: "auto",
                                  }} // Enables scrolling
                                >
                                  {countryInputs[index]
                                    ?.countrySelectedValue ? (
                                    <option value="">Select Country</option>
                                  ) : (
                                    <option value="" disabled>
                                      Select Country
                                    </option>
                                  )}

                                  {countryOriginData.map((currElem, index) => (
                                    <option
                                      key={index}
                                      value={currElem}
                                      className="p-2 h-10"
                                    >
                                      {currElem}
                                    </option>
                                  ))}
                                  <option value="Others (Please Specify)">
                                    Others (Please Specify)
                                  </option>
                                </select>

                                {/* Custom Input Field for "Others" */}
                                {countryInputs[index]?.countryOther && (
                                  <input
                                    type="text"
                                    value={
                                      countryInputs[index]
                                        ?.countryCustomInput || ""
                                    }
                                    onChange={(e) =>
                                      setCountryInputs((prev) => ({
                                        ...prev,
                                        [index]: {
                                          ...prev[index],
                                          countryCustomInput: e.target.value,
                                        },
                                      }))
                                    }
                                    onKeyDown={(e) =>
                                      handleCountryEnterPress(e, index)
                                    }
                                    placeholder="Enter custom country and press Enter"
                                    className="border border-gray-600 rounded px-2 sm:px-4 py-2 bg-gray-700 text-gray-200 mt-2"
                                    autoFocus
                                  />
                                )}

                                {/* Display Selected Custom Value */}
                                {countryInputs[index]?.countrySelectedValue &&
                                  !countryOriginData.includes(
                                    countryInputs[index]?.countrySelectedValue
                                  ) &&
                                  !countryInputs[index]?.countryOther && (
                                    <span className="mt-2 text-gray-300 text-sm sm:text-base">
                                      Selected:{" "}
                                      <span className="text-lg font-bold">
                                        {
                                          countryInputs[index]
                                            ?.countrySelectedValue
                                        }
                                      </span>
                                    </span>
                                  )}
                              </div>
                            ) : (
                              <span className="block w-full text-center text-sm sm:text-base">
                                {ingredient.country_origin || "N/A"}
                              </span>
                            )}
                          </td>
                          {role === "admin" && (
                            <>
                              <td className="border border-gray-500 px-2 sm:px-4 py-2">
                                <button
                                  onClick={() =>
                                    navigate(
                                      `/ingredient/${ingredient.ing_item_code}/${ingredient.rm_code}`
                                    )
                                  }
                                  className="bg-green-600 text-white py-1 px-3 rounded hover:bg-green-500 flex items-center justify-center mx-auto text-sm sm:text-base"
                                >
                                  <FaRegEdit className="mx-1" />
                                </button>
                              </td>
                              <td className="border border-gray-500 px-2 sm:px-4 py-2">
                                <button
                                  onClick={() =>
                                    deleteIngredient(ingredient.rm_code)
                                  }
                                  className="bg-red-500 text-white py-1 px-3 rounded hover:bg-red-600 flex items-center justify-center mx-auto text-sm sm:text-base"
                                >
                                  <FaTrash className="mr-1" />
                                </button>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Single Edit Button */}
              <div className="mt-4 text-right">
                {(role === "admin" || role === "user") && !editMode ? (
                  <button
                    onClick={handleEdit}
                    className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-500 flex items-center justify-center mx-auto"
                  >
                    <FaEdit className="mr-2" /> Edit
                  </button>
                ) : (
                  editMode && (
                    <div className="flex flex-row gap-4 justify-center">
                      <button
                        onClick={handleCancel}
                        className="bg-red-500 text-white py-2 px-4 rounded hover:bg-red-600 flex items-center justify-center"
                      >
                        <MdCancel className="mr-2" /> Cancel
                      </button>

                      <button
                        onClick={handleSave}
                        className="bg-green-600 text-white py-2 px-4 rounded hover:bg-blue-500 flex items-center justify-center"
                      >
                        <FaSave className="mr-2" /> Save
                      </button>
                    </div>
                  )
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default IngredientItem;
